<template>
  <article>
    <activity-stage>
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article slot="content">
        <div id="cards" role="list">
          <flippable-card
            v-for="(step, stepIndex) in currentRound.steps"
            :is-flipped="stepIndex < progress.stepIndex"
            :key="stepIndex"
            class="card"
            role="listitem"
          >
            <div slot="card-back" class="back-side">
              <img alt="The back side of a flippable card" :src="backOfCardPath" />
            </div>
            <div slot="card-front" class="front-side">
              <img
                :alt="step.answerImage?.image?.description"
                :src="step.answerImage?.image?.src"
              />
            </div>
          </flippable-card>
        </div>
        <div id="answers">
          <div
            class="answer"
            v-for="(step, stepIndex) in currentRound.steps"
            :key="`answer-${stepIndex}`"
          >
            <span>{{ getWordPart(step) }}</span>
            <blinking-cursor-text
              :text="currentRound.letterCombo"
              :text-step="stepIndex"
            ></blinking-cursor-text>
          </div>
        </div>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax, TimelineLite } from 'gsap';

import AssetLoader from 'mixins/AssetLoader';
import ErrorLogger from 'mixins/ErrorLogger';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';

import ActivityStage from 'components/activity/ActivityStage.vue';
import FlippableCard from 'components/activity/FlippableCard.vue';
import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';

import backOfCardPath from 'assets/images/face-flip-card-back.png';

export default {
  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, ErrorLogger],

  components: {
    ActivityStage,
    BlinkingCursorText,
    FlippableCard
  },

  data() {
    return {
      activityName: 'RhymeFlip',
      backOfCardPath,
      sfx: null
    };
  },

  beforeDestroy() {
    TweenMax.killAll();
  },

  methods: {
    async loadActivity() {
      this.createRounds();
      await this.loadImages();
      this.sfx = await this.loadSounds();
    },

    getKeyboardConfig() {
      const { keyboardType, roofs } = this.activityData.keyboard;
      return {
        keyboardType,
        roofs
      };
    },

    startActivity() {
      this.setRound();

      this.$nextTick(this.setActivityContainerHeight);
    },

    async loadImages() {
      this.rounds.forEach(({ steps: { answerImage } }) => this.queueImage(answerImage?.image?.src));

      return this.downloadImages();
    },

    async loadSounds() {
      this.rounds.forEach(({ steps }) =>
        steps.forEach(({ answerAudio }) => this.queueAudio(answerAudio.audio.src))
      );

      const cache = await this.downloadAudio();

      return this.rounds.map(({ steps }) =>
        steps.map(({ answerAudio }) => cache.get(answerAudio.audio.src))
      );
    },

    setRound() {
      this.setupNewRound({ stepCallback: this.stepCallback });
      this.showKeyboard({ enable: true });
      this.errorLoggerStart();
      TweenMax.delayedCall(0.5, () => {
        TweenMax.to('.activity-stage', 0.5, { opacity: 1 });
      });
    },

    handleCorrectKey(attempt) {
      this.sndType();
      this.incrementTypingIndex();

      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence, {
        skipAnnouncement: true
      });

      const { roundIndex, stepIndex } = this.progress;

      if (this.isEndOfCurrentSentence) {
        this.setScreenReaderAlert(`You have completed the word - ${this.currentStep.answer}`);
        this.sndBell();
        if (this.sfx[roundIndex][stepIndex]) {
          this.sfx[roundIndex][stepIndex].play();
        }

        if (this.progress.stepIndex >= this.totalStepsInRound - 1) {
          this.incrementStep();
          this.handleRoundEnd();
          return;
        }
        this.setCurrentStep({ stepCallback: this.stepCallback });
      }
    },

    handleRoundEnd() {
      if (this.isLastRound) {
        TweenMax.delayedCall(1, this.completeActivity);
      } else {
        const t = new TimelineLite();
        t.delay(1.3)
          .to('.activity-stage', 1, { opacity: 0 })
          .call(this.setRound);
      }
    },

    getWordPart(step) {
      return step.answer.split(this.currentRound.letterCombo)?.[0];
    },

    stepCallback() {
      return this.currentRound.letterCombo;
    }
  },
  watch: {
    currentStep(newVal) {
      if (newVal?.answer) {
        const numberOfRoundStepsLeft = this.currentRound.steps.length - this.progress.stepIndex;
        const timesOrtime = numberOfRoundStepsLeft === 1 ? 'time' : 'times';
        const instructions = `Type the letter combo - ${this.currentRound.letterCombo} - ${numberOfRoundStepsLeft} ${timesOrtime}`;
        setTimeout(() => {
          this.setScreenReaderAlert(this.getTouchHoldBasedInstructions(instructions));
        }, 1000);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
#cards {
  position: relative;
  display: inline-flex;
  justify-content: space-evenly;
  margin: 0px 5%;

  .card {
    text-align: center;
    margin: 0px 0.5%;

    img {
      width: 100%;
    }
  }
}

#answers {
  position: relative;
  display: flex;
  justify-content: space-evenly;
  text-align: center;

  @supports (-ms-ime-align: auto) {
    & {
      justify-content: space-around !important;
    }
  }

  .answer {
    font-size: 2vw;
    margin: 0 0.5%;

    span,
    p {
      display: inline-block;
      padding: 0;
      margin: 0;
    }
  }
}
</style>
